import React, { useState, useEffect, useRef, useTransition } from "react";
import Image from "./../image";
import CloseButton from "src/components/button/close-button";
import { useCrimeSceneStore } from "src/store/crimeSceneStore";
import useMdBreakpoint from "../../hooks/useMdBreakpoint";

import {
  AVATAR_DATA,
  CRIME_SCENE_STEPS,
  ALL_SCENES_DATA,
  CRIME_MC_QUESTIONS,
  CRIME_SCENE_OBJECTIVES,
} from "src/const/crimeSceneConst";

import { CrimeSceneSteps, MCQuestions } from "@/types/crime";
import { useAppStore } from "@/store/appStore";
import { CHAT_TYPE } from "@/types/navigation-mode";
import { useNavigationStore } from "@/store/navigationStore";
import { NavigationCheckpointData } from "@/types/navigation";

import {
  getAvatarImageByType,
  formatSecondsToHHmm,
} from "src/utils/simulation";
import { useTranslation } from "react-i18next";

export default function AIAgentPanel() {
  // Example single step content (replace with your data/props)
  const chatType = useAppStore((state) => state.chatType);
  const isMd = useMdBreakpoint();
  const contentRef = useRef<HTMLDivElement>(null);

  // for navigation
  const setProgress = useNavigationStore((s) => s.setProgress);
  const navigationSteps = useNavigationStore(
    (state) => state.navigationCheckpointDataArray
  );

  // for simulation
  const crimeSceneSteps = useCrimeSceneStore((state) => state.crimeSceneSteps);
  const setCrimeSceneSteps = useCrimeSceneStore(
    (state) => state.setCrimeSceneSteps
  );
  const sceneIndex = useCrimeSceneStore((state) => state.sceneIndex);
  const setSceneIndex = useCrimeSceneStore((state) => state.setSceneIndex);

  const [tabActive, setTabActive] = useState(1); // 1-based index
  const isFinishedAllMc = useCrimeSceneStore((state) => state.isFinishedAllMc);
  const { t } = useTranslation();

  useEffect(() => {
    setCrimeSceneSteps(CRIME_SCENE_STEPS);

    const contentElement = contentRef.current;
    if (chatType === CHAT_TYPE.SIMULATION) {
      setExpanded(true);
    } else {
      if (isMd) {
        setExpanded(true);
      } else {
        setExpanded(false);
      }
    }

    if (!contentElement || isMd) return;

    const handleScroll = () => {
      console.log("handle scroll");
      if (!isMd) {
        setExpanded(true);
      }
    };

    contentElement.addEventListener("scroll", handleScroll);

    return () => {
      if (contentElement) {
        contentElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isMd]);

  const [expanded, setExpanded] = useState(isMd);
  const isShownIncidentOccurence = useCrimeSceneStore(
    (state) => state.isShownIncidentOccurence
  );
  const setIsShownIncidentOccurence = useCrimeSceneStore(
    (state) => state.setIsShownIncidentOccurence
  );
  const resetProgress = useCrimeSceneStore((state) => state.resetProgress);

  const setShowNavAIPanel = useNavigationStore(
    (state) => state.setShowNavAIPanel
  );

  const getNavIcons = (step: NavigationCheckpointData) => {
    switch (step.nodeType) {
      case 1:
        return step.message.toLowerCase().includes("right")
          ? "/assets/icon_nav_1_right.png"
          : step.message.toLowerCase().includes("left")
          ? "/assets/icon_nav_1_left.png"
          : "/assets/icon_nav_1.png";
      default:
        return `/assets/icon_nav_${step.nodeType}.png`;
    }
  };

  const navigationContent = () => {
    return navigationSteps.map((step: NavigationCheckpointData, i: number) => (
      <div
        key={`${step.message}-${i}`}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation(); // This stops the click from reaching the parent

          if (!isMd) {
            setExpanded(false);
          }
          setShowNavAIPanel(false);

          setProgress(step.percentage);
        }}
        className="w-full p-5 border-b border-white/10
        transition cursor-pointer relative
        hover:bg-white/10"
      >
        {/* Row 1: image and title/arrow flex row */}
        <div className="flex items-center gap-4">
          <Image
            className="rounded-lg object-cover flex-shrink-0"
            src={
              i === navigationSteps.length - 1
                ? "/assets/icon_nav_destination.png"
                : getNavIcons(step)
            }
            alt={step.message}
            width={24}
            height={24}
          />
          <div className="flex-1 flex justify-between items-center">
            <div className="text-lg text-500">{step.message}</div>
            <span className="ml-1 flex items-center">
              <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
                <path
                  d="M9 18l6-6-6-6"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>
        </div>
      </div>
    ));
  };

  const crimeSceneContent = (index: number) => {
    const currentSceneMCQ = CRIME_MC_QUESTIONS.filter((c) => c.scene === index);
    const step: CrimeSceneSteps = crimeSceneSteps[index];

    return (
      <div
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation(); // This stops the click from reaching the parent
          // if (!isMd) {
          //   setExpanded(false);
          // }
          step.onClick();
          if (isFinishedAllMc) {
            setIsShownIncidentOccurence(false);
            setSceneIndex(index);
          }
        }}
        className={`w-full p-5 border-b border-white/10 transition cursor-pointer relative ${
          isFinishedAllMc ? "hover:bg-white/10" : ""
        }`}
      >
        {/* Row 1: image and title/arrow flex row */}
        <div className="flex items-center gap-4">
          {step ? (
            <img
              src={step.image}
              alt={step.title}
              className="rounded-lg w-[96px] h-[72px] object-cover flex-shrink-0"
            />
          ) : (
            <></>
          )}

          <div className="flex-1 flex justify-between items-center">
            <div className="text-lg text-500">{step ? t(step.title) : ""}</div>
            <span className="ml-1 flex items-center">
              {isFinishedAllMc ? (
                <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
                  <path
                    d="M9 18l6-6-6-6"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <></>
              )}
              {/*              <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
                <path
                  d="M9 18l6-6-6-6"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>*/}
            </span>
          </div>
        </div>
        {/* Metadata row */}
        <div className="flex flex-col gap-1 text-sm mt-3">
          <div className="flex items-center gap-2">
            <img
              src="assets/icon_calendar.svg"
              alt="Calendar icon"
              className="w-[24px] h-[24px]"
            />
            {step ? step.date : ""}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <img
              src="assets/icon_clock.svg"
              alt="Clock icon"
              className="w-[24px] h-[24px]"
            />
            {formatSecondsToHHmm(ALL_SCENES_DATA[index].startTime)}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <img
              src="assets/icon_pin.svg"
              alt="Pin icon"
              className="w-[24px] h-[24px]"
            />
            {step ? step.location : ""}
          </div>
          <div className="flex items-center gap-2 mt-3">
            {step ? (
              step.avatars.map((avatarName, avatarMapIndex) => (
                <div
                  key={`avatar_container${index}_${avatarName}${avatarMapIndex}`}
                  className="w-6 h-6 overflow-hidden rounded-full bg-white"
                >
                  <img
                    src={`assets/${getAvatarImageByType(avatarName)}`}
                    alt={`${avatarName} icon`}
                    className="object-cover -mt-0"
                  />
                </div>
              ))
            ) : (
              <></>
            )}
          </div>
        </div>
        <div>
          {ALL_SCENES_DATA[index].messages ? (
            ALL_SCENES_DATA[index].messages.map((message: any, index) => (
              <div key={index} className="whitespace-pre-line mt-4">
                {t(message)}
              </div>
            ))
          ) : (
            <></>
          )}
        </div>
        <hr className="border-t border-[#E5E9ED1F] my-4 w-full" />
        <div>{t("objective")}</div>
        <div className="mt-4">
          {t(CRIME_SCENE_OBJECTIVES[index].objectives) ?? ""}
        </div>
        {isFinishedAllMc ? (
          <>
            <div>
              {currentSceneMCQ.length > 0 ? (
                currentSceneMCQ.map((mc: MCQuestions, mcIndex) => (
                  <div key={mcIndex}>
                    <hr className="border-t border-[#E5E9ED1F] my-4 w-full" />
                    <div className="mt-1 text-[#E5E9EDB2]">
                      {t("mcq")} {mcIndex + 1}
                    </div>
                    <div className="mt-3 text-700 font-bold">{t(mc.question)}</div>
                    {mc.options.map((option: any, index: number) => (
                      <div key={index} className="mt-2 flex">
                        <span>{option.choice}</span>
                        <span className="ml-2">{t(option.answer)}</span>
                      </div>
                    ))}
                    <div className="rounded-lg bg-[#E5E9ED1F] mt-2 p-4">
                      <div>
                        {t("correct_answer")} {mc.correct}
                      </div>
                      <div>{t(mc.explanation)}</div>
                    </div>
                  </div>
                ))
              ) : (
                <></>
              )}
            </div>
          </>
        ) : (
          <></>
        )}
      </div>
    );

    // return crimeSceneSteps.map((step: CrimeSceneSteps, i: number) => (
    //   <div
    //     key={step.title}
    //     onClick={(e: React.MouseEvent) => {
    //       e.stopPropagation(); // This stops the click from reaching the parent

    //       if (!isMd) {
    //         setExpanded(false);
    //       }
    //       setIsShownIncidentOccurence(false);

    //       // special handling for each steps
    //       step.onClick();

    //       // const targetPercentage =
    //       //   simulationTimeData.items[i + 1].time - 0.5 < 0
    //       //     ? 0
    //       //     : (simulationTimeData.items[i + 1].time - 0.5) / SCENE_TOTAL_TIME;
    //       // console.log(targetPercentage);
    //       // resetProgress(targetPercentage);
    //       setSceneIndex(i);
    //     }}
    //     className="w-full p-5 border-b border-white/10 hover:bg-white/10 transition cursor-pointer relative"
    //   >
    //     {/* Row 1: image and title/arrow flex row */}
    //     <div className="flex items-center gap-4">
    //       <img
    //         src={step.image}
    //         alt={step.title}
    //         className="rounded-lg w-[96px] h-[72px] object-cover flex-shrink-0"
    //       />
    //       <div className="flex-1 flex justify-between items-center">
    //         <div className="text-lg text-500">{step.title}</div>
    //         <span className="ml-1 flex items-center">
    //           <svg width="22" height="22" fill="none" viewBox="0 0 24 24">
    //             <path
    //               d="M9 18l6-6-6-6"
    //               stroke="currentColor"
    //               strokeWidth={2}
    //               strokeLinecap="round"
    //               strokeLinejoin="round"
    //             />
    //           </svg>
    //         </span>
    //       </div>
    //     </div>
    //     {/* Metadata row */}
    //     <div className="flex flex-col gap-1 text-sm mt-3">
    //       <div className="flex items-center gap-2">
    //         <img
    //           src="assets/icon_calendar.svg"
    //           alt="Calendar icon"
    //           className="w-[24px] h-[24px]"
    //         />
    //         {step.date}
    //       </div>
    //       <div className="flex items-center gap-2 mt-3">
    //         <img
    //           src="assets/icon_clock.svg"
    //           alt="Clock icon"
    //           className="w-[24px] h-[24px]"
    //         />
    //         {step.time}
    //       </div>
    //       <div className="flex items-center gap-2 mt-3">
    //         <img
    //           src="assets/icon_pin.svg"
    //           alt="Pin icon"
    //           className="w-[24px] h-[24px]"
    //         />
    //         {step.location}
    //       </div>
    //       <div className="flex items-center gap-2 mt-3">
    //         {step.avatars.map((avatarName, avatarMapIndex) => (
    //           <div
    //             key={`avatar_container${i}_${avatarName}${avatarMapIndex}`}
    //             className="w-6 h-6 overflow-hidden rounded-full bg-white"
    //           >
    //             <img
    //               src={`assets/${getAvatarImageByType(avatarName)}`}
    //               alt={`${avatarName} icon`}
    //               className="object-cover -mt-0"
    //             />
    //           </div>
    //         ))}
    //       </div>
    //     </div>
    //     {/* Row 2: description below */}
    //     {/*        <div className="mt-2 text-base leading-snug mt-3">
    //       {typeof step.description === "string" ? (
    //         <span>{step.description}</span>
    //       ) : (
    //         step.description
    //       )}
    //     </div>*/}
    //     <div>
    //       {step.messages ? (
    //         step.messages.map((message: any, index) => (
    //           <div key={index} className="whitespace-pre-line mt-4">
    //             {message}
    //           </div>
    //         ))
    //       ) : (
    //         <></>
    //       )}
    //     </div>
    //     <hr className="border-t border-[#E5E9ED1F] my-4 w-full" />
    //     <div>Officer Objective</div>
    //     <div className="mt-4">{step.objectives ?? ""}</div>
    //     <hr className="border-t border-[#E5E9ED1F] my-4 w-full" />
    //     <div>
    //       {currentSceneMCQ.length > 0 ? (
    //         currentSceneMCQ.map((question: any, index) => (
    //           <div key={index}>
    //             <div className="mt-3">MCQ{index + 1}</div>
    //             {question.options.map((option: any, index: number) => (
    //               <div key={index} className="mt-2">
    //                 <span>{option.choice})</span>
    //                 <span className="ml-2">{option.answer}</span>
    //               </div>
    //             ))}
    //             <div className="rounded-lg bg-[#E5E9ED1F] mt-2 p-4">
    //               Content with large rounded corners and custom background
    //             </div>
    //             <hr className="border-t border-[#E5E9ED1F] my-4 w-full" />
    //           </div>
    //         ))
    //       ) : (
    //         <></>
    //       )}
    //     </div>
    //   </div>
    // ));
  };

  const SceneTabs = ({
    total = ALL_SCENES_DATA.length,
  }: {
    total?: number;
  }) => {
    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: total }).map((_, idx) => {
          const step = idx + 1;
          const isActive = step === tabActive;

          return (
            <div key={step} className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => {
                  console.log("set step " + step);
                  setTabActive(step);
                }}
                className={`
                  flex items-center justify-center w-[38px] h-[38px] rounded-full text-xs
                  border border-white/15 transition
                  ${isActive ? "bg-green-primary" : "bg-white/10 text-white/70"}
                `}
              >
                {step}
              </button>

              {/* connector line, omit after last */}
              {step !== total && (
                <span className="h-px w-3 bg-[#E5E9ED]/[0.06]" />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const getContent = () => {
    if (chatType === CHAT_TYPE.NAVIGATION) {
      return navigationContent();
    } else {
      return (
        <div>
          {/*TODO , show scene tab when finished all MC*/}

          {/* show selected scene if finished all MC , if not , show the current scene*/}
          {isFinishedAllMc
            ? crimeSceneContent(tabActive - 1)
            : crimeSceneContent(sceneIndex)}
        </div>
      );
    }
  };

  const closePanel = () => {
    if (chatType === CHAT_TYPE.NAVIGATION) {
      setShowNavAIPanel(false);
    } else {
      setIsShownIncidentOccurence(false);
    }
  };

  return (
    <div
      className={
        isMd
          ? `fixed right-0 transition-all duration-300 ease-in-out
        shadow-2xl
        ${
          expanded ? "h-[85vh]" : "h-[28vh] rounded-t-3xl "
        } max-w-[500px] flex flex-col z-50 text-default leading-[22px]`
          : `fixed left-0 right-0 mx-auto transition-all duration-300 ease-in-out
        shadow-2xl bottom-0 ${
          expanded ? "h-[85dvh]" : "h-[28dvh] rounded-t-3xl "
        } max-w-[100%] flex flex-col z-50 text-default leading-[22px]
          ${
            chatType === CHAT_TYPE.SIMULATION
              ? isShownIncidentOccurence
                ? "translate-y-0 opacity-100"
                : "translate-y-full pointer-events-none opacity-0"
              : ""
          }`
      }
      style={{
        minHeight: expanded ? "100%" : "200px",
        background: "linear-gradient(270deg, #04290C 0%, #022C30 100%)",
      }}
    >
      {/* Drag/Cursor handle */}
      <div
        id="ai-agent-drag-handle"
        className="flex items-center justify-between px-5 py-4"
        onClick={() => {
          if (chatType === CHAT_TYPE.NAVIGATION) {
            if (!isMd) {
              setExpanded((x) => !x);
            }
          }
        }}
      >
        <div className="flex items-center gap-2">
          <Image
            src="/assets/icon_ai_agent.png"
            alt="thumbnail"
            width={22}
            height={22}
          />
          <span className="text-500 primary-text-color">{t("ai_agent")}</span>
        </div>

        {/* Close Button */}
        <CloseButton
          withGlassBg={false}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation(); // This stops the click from reaching the parent
            // if (!isMd && expanded) {
            //   setExpanded(false);
            // } else {
            //   closePanel();
            // }
            closePanel();
          }}
          goBack={false}
          size={32}
        />
      </div>

      {/* Horizontal step list */}
      {isFinishedAllMc && chatType !== CHAT_TYPE.NAVIGATION ? (
        <div
          className="px-5 pb-2 overflow-x-auto [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none" }}
        >
          <SceneTabs />
        </div>
      ) : (
        <></>
      )}

      {/* Content */}
      <div
        ref={contentRef}
        className="overflow-y-auto [&::-webkit-scrollbar]:hidden flex-1"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {getContent()}
      </div>
    </div>
  );
}
